import React, { useState } from "react";
import { Check, Loader2, ShieldCheck } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const FORM_DEFS = {
  veteran: {
    label: "Veteran Interest",
    heading: "Tell us you're interested — that's all we need for now.",
    org: false,
    areas: [
      "EV & battery technician training",
      "Solar & energy storage",
      "Software & data",
      "Fleet & operations",
      "Not sure yet — just interested",
    ],
    note: "We will never ask for your SSN, DD-214, disability rating, or any sensitive documentation at this stage. Just your interest.",
  },
  training_partner: {
    label: "Training Partner",
    heading: "Help us build veteran training pathways.",
    org: true,
    areas: [
      "Technical training programs",
      "Apprenticeships",
      "Certification bodies",
      "Community college / trade school",
      "Other",
    ],
  },
  employer_partner: {
    label: "Employer Partner",
    heading: "Hire mission-ready veterans into the EV economy.",
    org: true,
    areas: [
      "EV service & repair roles",
      "Energy & solar roles",
      "Manufacturing roles",
      "Software / data roles",
      "Other",
    ],
  },
  esg_partner: {
    label: "ESG & Brand Partner",
    heading: "Put your brand behind veterans and the EVIQ forest.",
    org: true,
    areas: [
      "ESG sponsorship",
      "Brand partnership",
      "Tree-planting sponsorship",
      "Veteran sponsorship",
      "Other",
    ],
  },
};

const inputCls =
  "w-full rounded-xl border bg-white/[0.04] border-white/10 px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14]/50 transition-colors";

export const ImpactIntakeForms = ({ types = Object.keys(FORM_DEFS), defaultType = "veteran" }) => {
  const [active, setActive] = useState(types.includes(defaultType) ? defaultType : types[0]);
  const [form, setForm] = useState({ name: "", organization: "", email: "", location: "", area_of_interest: "", message: "" });
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const def = FORM_DEFS[active];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const switchTab = (t) => {
    setActive(t);
    setState("idle");
    setForm((f) => ({ ...f, area_of_interest: "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setState("sending");
    try {
      const res = await fetch(`${API_BASE}/api/impact/interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          form_type: active,
          name: form.name.trim(),
          email: form.email.trim(),
          organization: def.org ? form.organization.trim() || null : null,
          location: form.location.trim() || null,
          area_of_interest: form.area_of_interest || null,
          message: form.message.trim() || null,
        }),
      });
      setState(res.ok ? "done" : "error");
    } catch {
      setState("error");
    }
  };

  return (
    <div data-testid="impact-intake-forms">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {types.map((t) => (
          <button
            key={t}
            onClick={() => switchTab(t)}
            data-testid={`impact-form-tab-${t}`}
            className="rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-wider border transition-colors"
            style={
              active === t
                ? { background: "rgba(57,255,20,0.12)", borderColor: "rgba(57,255,20,0.5)", color: "#39FF14" }
                : { background: "transparent", borderColor: "rgba(255,255,255,0.12)", color: "#a1a1aa" }
            }
          >
            {FORM_DEFS[t].label}
          </button>
        ))}
      </div>

      <div className="text-white text-lg font-semibold mb-1">{def.heading}</div>
      {def.note && (
        <div
          className="flex items-start gap-2 rounded-xl border px-3 py-2.5 mb-4 text-[12px] leading-snug"
          style={{ borderColor: "rgba(0,229,255,0.3)", background: "rgba(0,229,255,0.06)", color: "#A9EDFF" }}
          data-testid="impact-form-privacy-note"
        >
          <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#00E5FF" }} />
          {def.note}
        </div>
      )}

      {state === "done" ? (
        <div
          className="rounded-2xl border p-6 text-center"
          style={{ borderColor: "rgba(57,255,20,0.35)", background: "rgba(57,255,20,0.06)" }}
          data-testid="impact-form-success"
        >
          <Check className="w-8 h-8 mx-auto mb-2" style={{ color: "#39FF14" }} />
          <div className="text-white font-semibold">You're on the list.</div>
          <div className="text-zinc-400 text-sm mt-1">
            Thank you — we'll reach out as the {def.label.toLowerCase()} program takes shape.
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input required value={form.name} onChange={set("name")} placeholder="Full name *" className={inputCls} data-testid="impact-form-name" />
            <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} data-testid="impact-form-email" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {def.org && (
              <input value={form.organization} onChange={set("organization")} placeholder="Organization" className={inputCls} data-testid="impact-form-organization" />
            )}
            <input value={form.location} onChange={set("location")} placeholder="Location (city, state)" className={inputCls} data-testid="impact-form-location" />
            <select
              value={form.area_of_interest}
              onChange={set("area_of_interest")}
              className={`${inputCls} ${form.area_of_interest ? "" : "text-zinc-600"}`}
              data-testid="impact-form-area"
            >
              <option value="">Area of interest</option>
              {def.areas.map((a) => (
                <option key={a} value={a} className="bg-black text-white">{a}</option>
              ))}
            </select>
          </div>
          <textarea
            value={form.message}
            onChange={set("message")}
            placeholder="Short message (optional)"
            rows={3}
            maxLength={1000}
            className={inputCls}
            data-testid="impact-form-message"
          />
          {state === "error" && (
            <div className="text-[12px]" style={{ color: "#FF6B57" }} data-testid="impact-form-error">
              Something went wrong — please check your email and try again.
            </div>
          )}
          <button
            type="submit"
            disabled={state === "sending"}
            className="inline-flex items-center gap-2 rounded-full px-7 py-3 font-mono text-[12px] uppercase tracking-wider font-bold transition-transform hover:scale-[1.02] disabled:opacity-60"
            style={{ background: "#39FF14", color: "#000" }}
            data-testid="impact-form-submit"
          >
            {state === "sending" && <Loader2 className="w-4 h-4 animate-spin" />}
            Submit interest
          </button>
        </form>
      )}
    </div>
  );
};
